import type { GameBankRules, GameBankSheetAnalysisRow, GameCheckStatus } from "@/Api/gameBank";

/**
 * Banco de Jogos (LOT-62): regras da planilha estilo Excel, sem React.
 *
 * As linhas têm um id estável só na tela (o banco guarda só as células), para que seleção, resultado da
 * conferência e desfazer sobrevivam a mover, inserir e excluir linhas. Todas as funções devolvem arrays
 * novos (a linha que não mudou mantém a mesma referência, para a grade não redesenhar à toa).
 */

export interface SheetRow {
  id: string;
  cells: string[];
}

export interface CellPos {
  r: number;
  c: number;
}

/** Retângulo selecionado, sempre com r1 <= r2 e c1 <= c2 (linhas em posição na tela) */
export interface CellRange {
  r1: number;
  r2: number;
  c1: number;
  c2: number;
}

export type SheetFilter = "all" | "problems" | "repeated" | "attention" | "invalid" | "incomplete";

export const MAX_ROWS = 500;

let sequence = 0;

export const newRowId = () => `r${Date.now().toString(36)}${(sequence++).toString(36)}`;

export const emptyCells = (columns: number) => Array.from({ length: columns }, () => "");

export const makeRow = (cells: string[]): SheetRow => ({ id: newRowId(), cells });

/** Linhas da API para a tela: exatamente uma célula por dezena */
export const toSheetRows = (rows: string[][], columns: number): SheetRow[] => {
  const list = rows.map((cells) => makeRow(Array.from({ length: columns }, (_, c) => cells[c] ?? "")));
  return list.length ? list : [makeRow(emptyCells(columns))];
};

export const isFilled = (cells: string[]) => cells.some((cell) => cell !== "");

export const rowSignature = (cells: string[]) => cells.join(",");

/** Quantidade de caracteres de uma dezena (Super Sete: dígito de 0 a 9) */
export const cellLength = (rules: GameBankRules) => (rules.max >= 10 ? 2 : 1);

/**
 * Completa a dezena sozinho quando só pode ser aquele número (ex.: Lotofácil, "3" só pode ser "03"),
 * para o cursor avançar sem precisar digitar o zero.
 */
export const settle = (digits: string, rules: GameBankRules): { value: string; done: boolean } => {
  const length = cellLength(rules);

  if (digits.length >= length) return { value: digits.slice(0, length), done: true };

  if (digits.length === 1 && Number(digits) * 10 > rules.max && rules.min <= Number(digits)) {
    return { value: digits.padStart(length, "0"), done: true };
  }

  return { value: digits, done: false };
};

/** Valor colado ou digitado vira dezena: só dígitos, no tamanho da loteria, "5" vira "05" */
export const normalizeToken = (token: string, rules: GameBankRules) => {
  const length = cellLength(rules);
  const digits = token.replace(/\D/g, "").slice(0, length);
  return digits.length === 1 && length === 2 ? digits.padStart(2, "0") : digits;
};

/** Células com número fora da faixa ou repetido na linha (loterias sem posição) */
export const rowProblems = (cells: string[], rules: GameBankRules): Record<number, boolean> => {
  const problems: Record<number, boolean> = {};
  const seen = new Map<number, number>();

  cells.forEach((cell, column) => {
    if (cell === "") return;
    const value = Number(cell);

    if (value < rules.min || value > rules.max) problems[column] = true;

    if (!rules.ordered) {
      if (seen.has(value)) {
        problems[column] = true;
        problems[seen.get(value) as number] = true;
      }
      seen.set(value, column);
    }
  });

  return problems;
};

export const hasLocalProblem = (cells: string[], rules: GameBankRules) => Object.keys(rowProblems(cells, rules)).length > 0;

export const isIncomplete = (cells: string[]) => isFilled(cells) && cells.some((cell) => cell === "");

export const normalizeRange = (a: CellPos, b: CellPos): CellRange => ({
  r1: Math.min(a.r, b.r),
  r2: Math.max(a.r, b.r),
  c1: Math.min(a.c, b.c),
  c2: Math.max(a.c, b.c),
});

export const rangeSize = (range: CellRange) => {
  const rows = range.r2 - range.r1 + 1;
  const cols = range.c2 - range.c1 + 1;
  return { rows, cols, cells: rows * cols };
};

export const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/** Troca as células de uma linha só se algo mudou (mantém a referência da linha quando não muda) */
const withCells = (row: SheetRow, cells: string[]): SheetRow =>
  cells.every((cell, index) => cell === row.cells[index]) ? row : { ...row, cells };

// ---------------------------------------------------------------- área de transferência

/** Seleção em texto separado por tab, uma linha por jogo (o formato que o Excel e o Google Planilhas usam) */
export const toTsv = (rows: SheetRow[], indexes: number[], c1: number, c2: number) =>
  indexes.map((index) => rows[index].cells.slice(c1, c2 + 1).join("\t")).join("\r\n");

/**
 * Texto colado para matriz de dezenas. Linhas por quebra de linha; células por tab (Excel) ou, sem tab,
 * por espaço, vírgula ou ponto e vírgula ("01 02 03").
 */
export const parseClipboard = (text: string, rules: GameBankRules): string[][] => {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");

  while (lines.length > 0 && lines[lines.length - 1].trim() === "") lines.pop();

  return lines.map((line) => {
    const tokens = line.includes("\t") ? line.split("\t") : line.trim() === "" ? [""] : line.trim().split(/[;,\s]+/);
    return tokens.map((token) => normalizeToken(token, rules));
  });
};

export interface PasteResult {
  rows: SheetRow[];
  /** Área escrita, em posição na tela */
  range: CellRange;
  /** Havia mais colunas do que a planilha tem */
  trimmed: boolean;
  /** Passou do limite de linhas (ou da última linha visível com filtro) */
  capped: boolean;
}

/**
 * Cola a matriz a partir do canto da seleção. Um valor só, com uma área selecionada, preenche a área.
 * `visible` liga a posição na tela à posição real (com filtro, só as linhas visíveis recebem o colar).
 * Sem filtro, colar além da última linha cria linhas novas, até o limite.
 */
export const pasteMatrix = (
  rows: SheetRow[],
  visible: number[],
  selection: CellRange,
  matrix: string[][],
  columns: number,
  canAppend: boolean
): PasteResult => {
  const next = rows.slice();
  const map = visible.slice();
  let trimmed = false;
  let capped = false;

  const setCell = (visibleRow: number, column: number, value: string) => {
    const index = map[visibleRow];
    const cells = next[index].cells.slice();
    cells[column] = value;
    next[index] = withCells(next[index], cells);
  };

  const single = matrix.length === 1 && matrix[0].length === 1;

  if (single && rangeSize(selection).cells > 1) {
    for (let r = selection.r1; r <= selection.r2; r++) {
      for (let c = selection.c1; c <= selection.c2; c++) setCell(r, c, matrix[0][0]);
    }
    return { rows: next, range: selection, trimmed, capped };
  }

  let written = 0;
  let widest = 0;

  for (let i = 0; i < matrix.length; i++) {
    const visibleRow = selection.r1 + i;

    if (visibleRow >= map.length) {
      if (!canAppend || next.length >= MAX_ROWS) {
        capped = true;
        break;
      }
      next.push(makeRow(emptyCells(columns)));
      map.push(next.length - 1);
    }

    matrix[i].forEach((value, j) => {
      const column = selection.c1 + j;
      if (column >= columns) {
        trimmed = true;
        return;
      }
      setCell(visibleRow, column, value);
      widest = Math.max(widest, j + 1);
    });
    written++;
  }

  return {
    rows: next,
    range: {
      r1: selection.r1,
      r2: selection.r1 + Math.max(written, 1) - 1,
      c1: selection.c1,
      c2: Math.min(columns - 1, selection.c1 + Math.max(widest, 1) - 1),
    },
    trimmed,
    capped,
  };
};

/** Apaga o conteúdo da área (linhas em posição na tela) */
export const clearRange = (rows: SheetRow[], visible: number[], selection: CellRange): SheetRow[] => {
  let changed = false;
  const next = rows.slice();

  for (let r = selection.r1; r <= selection.r2; r++) {
    const index = visible[r];
    if (index === undefined) continue;
    const cells = next[index].cells.slice();
    for (let c = selection.c1; c <= selection.c2; c++) cells[c] = "";
    const row = withCells(next[index], cells);
    if (row !== next[index]) {
      next[index] = row;
      changed = true;
    }
  }

  return changed ? next : rows;
};

/** Grava uma célula; devolve o mesmo array quando o valor não mudou */
export const setCell = (rows: SheetRow[], index: number, column: number, value: string): SheetRow[] => {
  if (rows[index]?.cells[column] === value) return rows;
  const next = rows.slice();
  const cells = next[index].cells.slice();
  cells[column] = value;
  next[index] = { ...next[index], cells };
  return next;
};

// ---------------------------------------------------------------- linhas

/** Insere `count` linhas vazias na posição `at` (respeita o limite) */
export const insertRows = (rows: SheetRow[], at: number, count: number, columns: number) => {
  const room = Math.max(0, Math.min(count, MAX_ROWS - rows.length));
  const fresh = Array.from({ length: room }, () => makeRow(emptyCells(columns)));
  return { rows: [...rows.slice(0, at), ...fresh, ...rows.slice(at)], inserted: room };
};

/** Exclui as linhas; a planilha nunca fica sem linha nenhuma */
export const deleteRows = (rows: SheetRow[], indexes: number[], columns: number): SheetRow[] => {
  const drop = new Set(indexes);
  const next = rows.filter((_, index) => !drop.has(index));
  return next.length ? next : [makeRow(emptyCells(columns))];
};

/** Duplica as linhas logo depois da última selecionada (respeita o limite) */
export const duplicateRows = (rows: SheetRow[], indexes: number[]) => {
  const sorted = [...indexes].sort((a, b) => a - b);
  const room = Math.max(0, Math.min(sorted.length, MAX_ROWS - rows.length));
  const copies = sorted.slice(0, room).map((index) => makeRow(rows[index].cells.slice()));
  const at = sorted[sorted.length - 1] + 1;
  return { rows: [...rows.slice(0, at), ...copies, ...rows.slice(at)], inserted: room, at };
};

/** Move o bloco r1..r2 uma posição para cima (-1) ou para baixo (+1). null quando já está na borda */
export const moveRowsBy = (rows: SheetRow[], r1: number, r2: number, delta: -1 | 1): SheetRow[] | null => {
  if (r1 + delta < 0 || r2 + delta >= rows.length) return null;
  return moveRowsTo(rows, r1, r2, delta < 0 ? r1 - 1 : r2 + 2).rows;
};

/**
 * Move o bloco r1..r2 para antes da linha `target` (0..length, em posição antes da mudança).
 * Devolve onde o bloco começa depois de mover.
 */
export const moveRowsTo = (rows: SheetRow[], r1: number, r2: number, target: number) => {
  const block = rows.slice(r1, r2 + 1);
  const rest = [...rows.slice(0, r1), ...rows.slice(r2 + 1)];
  const at = clamp(target > r2 ? target - block.length : target, 0, rest.length);
  return { rows: [...rest.slice(0, at), ...block, ...rest.slice(at)], start: at };
};

// ---------------------------------------------------------------- colunas

/** Move as colunas c1..c2 uma posição para a esquerda (-1) ou direita (+1) em todas as linhas */
export const moveColumnsBy = (rows: SheetRow[], c1: number, c2: number, delta: -1 | 1, columns: number): SheetRow[] | null => {
  if (c1 + delta < 0 || c2 + delta >= columns) return null;

  return rows.map((row) => {
    const cells = row.cells.slice();
    const block = cells.splice(c1, c2 - c1 + 1);
    cells.splice(c1 + delta, 0, ...block);
    return withCells(row, cells);
  });
};

// ---------------------------------------------------------------- arrumar a planilha

/** Ordena as dezenas de cada linha (vazias no fim). Não vale para loterias posicionais */
export const sortRowNumbers = (rows: SheetRow[], indexes: number[]): SheetRow[] => {
  const targets = new Set(indexes);
  let changed = false;

  const next = rows.map((row, index) => {
    if (!targets.has(index)) return row;
    const filled = row.cells.filter((cell) => cell !== "").sort((a, b) => Number(a) - Number(b));
    const cells = [...filled, ...Array.from({ length: row.cells.length - filled.length }, () => "")];
    const updated = withCells(row, cells);
    if (updated !== row) changed = true;
    return updated;
  });

  return changed ? next : rows;
};

/** Tira as linhas vazias (fica ao menos uma) */
export const removeEmptyRows = (rows: SheetRow[], columns: number) => {
  const next = rows.filter((row) => isFilled(row.cells));
  return { rows: next.length ? next : [makeRow(emptyCells(columns))], removed: rows.length - next.length };
};

/** Chave do jogo: sem posição, a ordem das dezenas não importa */
export const gameKey = (cells: string[], ordered: boolean) => {
  const values = cells.filter((cell) => cell !== "").map(Number);
  return (ordered ? values : [...values].sort((a, b) => a - b)).join("-");
};

/** Tira as linhas repetidas dentro da planilha, mantendo a primeira */
export const removeDuplicateRows = (rows: SheetRow[], ordered: boolean) => {
  const seen = new Set<string>();
  const next = rows.filter((row) => {
    if (!isFilled(row.cells)) return true;
    const key = gameKey(row.cells, ordered);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { rows: next, removed: rows.length - next.length };
};

// ---------------------------------------------------------------- localizar e filtrar

/** Células (em posição na tela) com a dezena procurada */
export const findMatches = (rows: SheetRow[], visible: number[], query: string): CellPos[] => {
  const digits = query.replace(/\D/g, "");
  if (digits === "") return [];
  const target = Number(digits);
  const matches: CellPos[] = [];

  visible.forEach((index, r) => {
    rows[index].cells.forEach((cell, c) => {
      if (cell !== "" && Number(cell) === target) matches.push({ r, c });
    });
  });

  return matches;
};

const ATTENTION: GameCheckStatus[] = ["attention", "high"];

/**
 * A linha entra no filtro? `status` é o resultado da conferência que ainda vale para a linha (sem ele,
 * só os problemas da própria tela contam). Linhas vazias só aparecem em "Todas".
 */
export const matchesFilter = (cells: string[], filter: SheetFilter, rules: GameBankRules, status?: GameCheckStatus) => {
  if (filter === "all") return true;
  if (!isFilled(cells)) return false;

  const incomplete = isIncomplete(cells);
  const local = hasLocalProblem(cells, rules);

  switch (filter) {
    case "repeated":
      return status === "repeated";
    case "attention":
      return status !== undefined && ATTENTION.includes(status);
    case "invalid":
      return local || status === "invalid";
    case "incomplete":
      return incomplete;
    case "problems":
      return incomplete || local || status === "repeated" || status === "invalid" || (status !== undefined && ATTENTION.includes(status));
    default:
      return true;
  }
};

// ---------------------------------------------------------------- resultado da conferência

/** Resultado da conferência de uma linha, preso ao id da linha (sobrevive a mover, inserir e excluir) */
export interface RowResult {
  res: GameBankSheetAnalysisRow;
  /** Células como estavam na conferência; se mudaram, o resultado ficou velho */
  signature: string;
  /** Linha da planilha comparada (quando a coincidência é com outra linha), para mostrar a posição atual */
  matchRowId?: string;
}

/**
 * Liga o resultado da conferência às linhas que foram conferidas. `line` e `match.row` vêm da API em
 * posição na planilha no momento da conferência.
 */
export const buildResults = (analysis: GameBankSheetAnalysisRow[], rows: SheetRow[]): Record<string, RowResult> => {
  const results: Record<string, RowResult> = {};

  analysis.forEach((res) => {
    const row = rows[res.line];
    if (!row) return;
    const matched = res.match?.source === "sheet" && res.match.row ? rows[res.match.row - 1] : undefined;
    results[row.id] = { res, signature: rowSignature(row.cells), matchRowId: matched?.id };
  });

  return results;
};

/** Status da conferência que ainda vale para a linha (as células não mudaram desde a conferência) */
export const freshStatus = (row: SheetRow, results: Record<string, RowResult>): GameCheckStatus | undefined => {
  const result = results[row.id];
  return result && result.signature === rowSignature(row.cells) ? result.res.status : undefined;
};
